/*
 * @Author: 张印 hbzyin@163.com
 * @Date: 2023-07-27 19:21:22
 * @LastEditors: 张印 hbzyin@163.com
 * @LastEditTime: 2023-08-18 16:49:40
 * @FilePath: /mall-fe/src/libs/universal.ts
 * @Description: 云存储资源相关解析
 *
 */
// 这个 ts 里的代码，需要同步拷贝到 webapp ifs-eui mall-ui 三个项目里
// 在不用 npm 的时候，已经是最优解了
// 如果在非 mall-fe 的项目里看到这个文件，不要直接修改
// 改代码记得加单元测试

export type OssProvider = 'obs' | 'aliyun';
type OssPerm = 'public' | 'private';
export type OssConfig = {
  ossProvider: OssProvider;
  publicBucketName: string;
  privateBucketName: string;
  wzgjOssProvider?: string;
  wzgjPublicOssProvider?: string;
};

/**
 * DO_NOT_MANUALLY_MODIFY
 * 主要决定 bucket 的公有/私有
 * @param {string} url 相对路径
 */
export function getOssPerm(url: string): OssPerm {
  if (url.startsWith('/')) {
    // eslint-disable-next-line no-param-reassign
    url = url.substring(1);
  }

  if (url.startsWith('public')) {
    return 'public';
  } else {
    return 'private';
  }
}

/**
 * DO_NOT_MANUALLY_MODIFY
 * 主要决定资源到底放在哪个云上
 * @param {string} url 相对路径
 */
export function getOssProvider(
  absoluteUrl: string,
  config: OssConfig,
): {
  provider: OssProvider;
  legacy: boolean;
} {
  let url = absoluteUrl || '';
  if (config.ossProvider === 'obs') {
    return { provider: 'obs', legacy: false };
  } else if (config.ossProvider === 'aliyun') {
    if (url.startsWith('/')) {
      // remove leading slash
      url = url.substring(1);
    }

    if (
      urlStartsWith(url, config.publicBucketName) ||
      urlStartsWith(url, config.privateBucketName)
    ) {
      return { provider: 'aliyun', legacy: false };
    } else {
      return { provider: 'obs', legacy: true };
    }
  } else {
    // default obs
    return { provider: 'obs', legacy: false };
  }
}

function urlStartsWith(url: string, bucketName: string) {
  const names = bucketName.split(',');
  for (const n of names) {
    if (url.startsWith(n)) {
      return true;
    }
  }
  return false;
}

/**
 * DO_NOT_MANUALLY_MODIFY
 * 不能用 `getOssProvider`，绝对路径不适用
 * 又再hack了一下
 */
export function isAbsolutePathAliyun(url: string, ossConfig: OssConfig): boolean {
  if (ossConfig.ossProvider === 'aliyun') {
    const { privateBucketName, publicBucketName, wzgjOssProvider, wzgjPublicOssProvider } =
      ossConfig || {};
    if (
      urlIncludes(url, privateBucketName) ||
      urlIncludes(url, publicBucketName) ||
      urlIncludes(url, wzgjOssProvider) ||
      urlIncludes(url, wzgjPublicOssProvider)
    ) {
      return true;
    } else {
      return false;
    }
  } else {
    return false;
  }
}

function urlIncludes(url: string, bucketName: string) {
  if (!url) {
    return false;
  }

  const names = bucketName.split(',');
  for (const n of names) {
    if (url.includes(n)) {
      return n;
    }
  }
  return false;
}

/**
 * DO_NOT_MANUALLY_MODIFY
 * 1. 由 provider 决定走哪一个云存储（阿里云 || 平安云）
 * 2. private bucket 服务端做302跳转，public bucket 客户端直接访问，所以有不同的路径拼法
 * 3. *特例*是全局 provider 是阿里云的时候，还是有部分 pu blic 资源放在平安云上
 *
 * @param {string} url 相对路径
 */
export function getOssUrl(
  url: string,
  conf: OssConfig & {
    publicHost: string;
    privateHost: string;
    legacyHost: string;
  },
) {
  // curr (not-legacy) oss configs
  const { publicHost, privateHost, legacyHost } = conf;
  if (!url) {
    return url;
  }

  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // ensure leading slash
  if (!url.startsWith('/')) {
    // eslint-disable-next-line no-param-reassign
    url = '/' + url;
  }

  const p = getOssProvider(url, conf);
  const perm = getOssPerm(url);

  if (p.provider === 'obs') {
    if (perm === 'private') {
      return `${privateHost}${url}`;
    } else {
      if (p.legacy) {
        return `${legacyHost}${url}`;
      } else {
        return `${publicHost}${url}`;
      }
    }
  } else if (p.provider === 'aliyun') {
    if (perm === 'private') {
      return `${privateHost}${url}`;
    } else {
      if (url.startsWith('/')) {
        // eslint-disable-next-line no-param-reassign
        url = url.substring(1);
      }
      // aliyun's public:  bucket.endpoint/dir/file
      const segments = url.split('/');
      if (segments.length < 2) {
        return `${privateHost}/${url}`;
      }

      const bucketName = segments.splice(0, 1);
      const uri = new URL(publicHost);
      const host = uri.hostname;
      const newHost = `${bucketName}.${host}`;
      uri.hostname = newHost;
      uri.pathname = '/' + segments.join('/');
      return uri.toString();
    }
  } else {
    return url;
  }
}

/**
 * DO_NOT_MANUALLY_MODIFY
 * 拼参数让对象存储剪裁图片
 * https://yun.pingan.com/ssr/help/storage/obs/api.API_Description
 * @param url absolute path
 * @param width width
 * @param height height
 * @param mode mode
 * 0：限定缩略图的长边最多为 ，短边最多为 ，进行等比缩放，不裁剪。如果只指定 w 参数则表示限定长边（短边自适应），只指定 h 参数则表示限定短边（长边自适应）。
 * 1：限定缩略图的宽最少为 ，高最少为 ，进行等比缩放，居中裁剪。转后的缩略图通常恰好是 x 的大小（有一个边缩放的时候会因为超出矩形框而被裁剪掉多余部分）。如果只指定 w 参数或只指定 h 参数，代表限定为长宽相等的正方图。
 * 2：限定缩略图的宽最多为 ，高最多为 ，进行等比缩放，不裁剪。如果只指定 w 参数则表示限定宽（长自适应），只指定 h 参数则表示限定长（宽自适应）。它和模式0类似，区别只是限定宽和高，不是限定长边和短边。从应用场景来说，模式0适合移动设备上做缩略图，模式2适合PC上做缩略图。
 * 3：限定缩略图的宽最少为 ，高最少为 ，进行等比缩放，不裁剪。如果只指定 w 参数或只指定 h 参数，代表长宽限定为同样的值。你可以理解为模式1是模式3的结果再做居中裁剪得到的。
 * 4：限定缩略图的长边最少为 ，短边最少为 ，进行等比缩放，不裁剪。如果只指定 w 参数或只指定 h 参数，表示长边短边限定为同样的值。这个模式很适合在手持设备做图片的全屏查看（把这里的长边短边分别设为手机屏幕的分辨率即可），生成的图片尺寸刚好充满整个屏幕（某一个边可能会超出屏幕）。
 * 5：限定缩略图的长边最少为 ，短边最少为 ，进行等比缩放，居中裁剪。如果只指定 w 参数或只指定 h 参数，表示长边短边限定为同样的值。同上模式4，但超出限定的矩形部分会被裁剪。 `width:` 图片宽度，非负数，有效值（0-6000)
 */
export function resizeImage(
  url: string,
  width: number,
  height: number,
  ossConfig: OssConfig,
  mode: 0 | 1 | 2 | 3 | 4 | 5 = 0,
): string {
  if (!url) {
    return url;
  }

  if (!url.startsWith('http')) {
    return url;
  }

  if (!isAbsolutePathAliyun(url, ossConfig)) {
    // obs 的 `feature-not-bug`：bucket 里有自建文件夹，需要自己转义个`%2f`才能正常剪裁
    // eg:
    // http://domain/bucket/dir/foo/imageView => 404
    // http://domain/bucket/dir%2ffoo/imageView => 200
    const protocalIdx = url.indexOf('//');
    const domainIdx = url.indexOf('/', protocalIdx + 2);
    const bucketIdx = url.indexOf('/', domainIdx + 1);
    const objectStr = url.substring(bucketIdx + 1).replace(/\//g, '%2f');
    const parsed = url.substring(0, bucketIdx + 1) + objectStr;
    // eslint-disable-next-line no-param-reassign
    url = parsed;
  }

  return resizeFn(url, width, height, mode, ossConfig);
}

/**
 * DO_NOT_MANUALLY_MODIFY
 * 阿里云的剪裁和平安云的剪裁对不上，阿里云版本写死了mode=0对应的剪裁方式
 * @param {string} url 绝对路径
 */
function resizeFn(url: string, w: number, h: number, mode: number, ossConfig: OssConfig) {
  if (isAbsolutePathAliyun(url, ossConfig)) {
    return `${url}?x-oss-process=image/resize,m_lfit,h_${h},w_${w}`;
  } else {
    return `${url}/imageView/${mode}/w/${w}/h/${h}`;
  }
}

/**
 * 阿里云 public 读的资源都不支持预览只能下载，在 iframe 里也不行
 * CAUTION: 会占用服务器带宽和流量
 * /sso/ali-proxy 这个 endpoint 在 webapp 里（还需要 ENV=zjga）
 * @param url 绝对路径
 * @param {OssConfig} ossConfig
 */
export function getProxyUrl(url: string, ossConfig: OssConfig) {
  if (isAbsolutePathAliyun(url, ossConfig) && urlIncludes(url, ossConfig.publicBucketName)) {
    return `/sso/ali-proxy?q=${encodeURIComponent(url)}`;
  } else {
    return url;
  }
}

/**
 * 阿里云的 public 资源不支持 content-disposition，需要还原成 private domain 让后台签名
 * @param url 绝对/相对无所谓
 */
export function buildDownloadUrl(
  url: string,
  filename: string,
  conf: OssConfig & {
    privateHost: string;
  },
): string {
  if (!url) {
    return url;
  }

  if (url.startsWith('http://') || url.startsWith('https://')) {
    const { pathname } = new URL(url);
    // absolute => relative
    if (isAbsolutePathAliyun(url, conf)) {
      // ali
      const pbn = urlIncludes(url, conf.publicBucketName);
      if (pbn) {
        // public: path prefix is in hostname
        // eslint-disable-next-line no-param-reassign
        url = `${pbn}${pathname}`;
      } else {
        // private
        // eslint-disable-next-line no-param-reassign
        url = pathname;
      }
    } else {
      // pingan
      // eslint-disable-next-line no-param-reassign
      url = pathname;
    }
  } else {
    // relative ok
  }

  // ensure leading slash
  if (!url.startsWith('/')) {
    // eslint-disable-next-line no-param-reassign
    url = '/' + url;
  }

  const dispositionHeaderValue = `attachment; filename* = UTF-8''${encodeURI(filename)}`;
  const { privateHost } = conf;

  // always the privateHost, server got your back
  return `${privateHost}${url}?response-content-disposition=${encodeURIComponent(
    dispositionHeaderValue,
  )}`;
}
